import React from 'react';

const FinanceProducts = () => {
  // Finance products data
  const financeProducts = [
    {
      id: 7,
      name: "Working Capital",
      slug: "working-capital-for-business",
      description: "Working capital can help you finance business needs, including startup funding, tenant improvements, equipment and inventory purchases, and more",
      icon: "finance",
      sortOrder: 1,
      isActive: true,
      loanRange: "$50,000 - $250,000",
      features: ["Startup funding", "Inventory purchases", "Cash flow management"]
    },
    {
      id: 3,
      name: "Commercial Real Estate Financing",
      slug: "commercial-real-estate-financing", 
      description: "Commercial Real Estate Financing",
      icon: "business",
      sortOrder: 3,
      isActive: true,
      loanRange: "Up to $5M",
      features: ["Property purchase", "Refinancing", "Construction loans"]
    },
    {
      id: 5,
      name: "Non-profit Financing",
      slug: "nonprofit-financing",
      description: "Shariah-compliant financing for non-profit organizations",
      icon: "mosque",
      sortOrder: 5,
      isActive: true,
      loanRange: "Up to $5M",
      features: ["Mosque construction", "Community centers", "Educational facilities"]
    },
    {
      id: 8,
      name: "Equipment Financing",
      slug: "equipment-financing",
      description: "Finance business equipment, machinery, and technology with halal financing options",
      icon: "equipment",
      sortOrder: 2,
      isActive: true,
      loanRange: "$50,000 - $250,000",
      features: ["Restaurant equipment", "Medical equipment", "Manufacturing machinery"]
    }
  ];

  const getIconComponent = (iconName) => {
    const iconMap = {
      business: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      finance: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
      mosque: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
        </svg>
      ),
      equipment: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    };
    return iconMap[iconName] || iconMap.business;
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('loan-form');
    if (formElement) {
      const formPosition = formElement.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: formPosition - 20,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a4a1a] mb-6">
            Halal Business Financing
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Choose from our range of riba-free financing options designed for your business needs. 
            All loans are verified by Islamic scholars and follow Shariah principles.
          </p>
        </div>

        {/* Finance Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {financeProducts.map((product, index) => {
            // Color schemes based on HalalCred logo colors
            const colorSchemes = [
              {
                bg: 'bg-gradient-to-br from-[#1a4a1a] to-[#2d5a2d]',
                icon: 'text-white',
                accent: 'text-[#1a4a1a]'
              },
              {
                bg: 'bg-gradient-to-br from-[#fbbf24] to-[#f59e0b]',
                icon: 'text-white',
                accent: 'text-[#f59e0b]'
              },
              {
                bg: 'bg-gradient-to-br from-[#059669] to-[#047857]',
                icon: 'text-white',
                accent: 'text-[#059669]'
              },
              {
                bg: 'bg-gradient-to-br from-[#7c3aed] to-[#6d28d9]',
                icon: 'text-white',
                accent: 'text-[#7c3aed]'
              }
            ];
            
            const colors = colorSchemes[index % colorSchemes.length];
            
            return (
            <div
              key={product.id}
              className="group bg-white rounded-2xl border border-[#1a4a1a]/10 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer overflow-hidden"
              onClick={scrollToForm}
            >
              {/* Card Header */}
              <div className="p-8">
                {/* Icon */}
                <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <div className={colors.icon}>
                    {getIconComponent(product.icon)}
                  </div>
                </div>

                {/* Product Name */}
                <h3 className="text-xl font-bold text-[#1a4a1a] mb-4 group-hover:text-[#2d5a2d] transition-colors leading-tight">
                  {product.name}
                </h3>
                
                {/* Loan Range */}
                <div className="mb-6">
                  <div className="text-sm text-slate-600 mb-2 font-semibold">Available Amount</div>
                  <div className="text-2xl font-bold text-[#1a4a1a] leading-tight">
                    {product.loanRange}
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3">
                  {product.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center text-sm text-slate-700">
                      <svg className="w-4 h-4 text-emerald-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <button
            onClick={scrollToForm}
            className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-[#1a4a1a] to-[#2d5a2d] text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
          >
            Explore Halal Funding
            <svg className="w-5 h-5 ml-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default FinanceProducts;